import React from "react";
import { formatNumber } from "@/lib/utils";
import {
  displayCriterionName,
  formatCriterionStandard,
  isQuantitativeCriterion,
} from "@/components/quality/qc-form/CriteriaTable";
import { QC_RESULT_LABELS, checklistItemsOf } from "@/components/quality/qc-form/checklist";

/**
 * Khối dùng chung cho bản in phiếu QC (IQC / FQC / CoA), A4 dọc:
 * thông tin chung 4 cột · 1 hàng lấy mẫu · 1 bảng chỉ tiêu 7 cột (9pt) · lỗi phát hiện (khi có) · 1 hàng kết luận · ký.
 */

const SEVERITY_LABEL: Record<string, string> = { CRITICAL: "Nghiêm trọng", MAJOR: "Nặng", MINOR: "Nhẹ" };

export function checklistOf(raw: any): any[] {
  return checklistItemsOf(raw);
}

export function QcPrintInfoGrid({ items }: { items: Array<[string, React.ReactNode]> }) {
  return (
    <div className="mb-2 grid grid-cols-4 border-l border-t border-black text-[9pt]" data-testid="qc-print-info">
      {items.map(([label, value]) => (
        <div key={label} className="min-w-0 border-b border-r border-black px-1.5 py-0.5">
          <div className="text-[7.5pt] text-slate-600">{label}</div>
          <div className="font-semibold break-words">{value === undefined || value === null || value === "" ? "—" : value}</div>
        </div>
      ))}
    </div>
  );
}

export interface QcPrintSamplingProps {
  lotSize?: number | null;
  unit?: string | null;
  mode?: string | null;
  level?: string | null;
  aqlMajor?: number | null;
  aqlMinor?: number | null;
  sampleSize?: number | null;
  acMajor?: number | null;
  reMajor?: number | null;
  acMinor?: number | null;
  reMinor?: number | null;
}

export function QcPrintSamplingRow(p: QcPrintSamplingProps) {
  const isFull = String(p.mode || "").toUpperCase() === "FULL";
  const num = (v: any) => (v === null || v === undefined || v === "" ? "—" : formatNumber(Number(v)));
  const cells: Array<[string, string]> = [
    ["Cỡ lô", `${num(p.lotSize)}${p.unit ? ` ${p.unit}` : ""}`],
    ["Cách kiểm", isFull ? "100%" : "AQL"],
    ...(isFull
      ? ([["Lỗi bị loại", p.reMinor ? "Mọi lỗi" : "Nặng trở lên"]] as Array<[string, string]>)
      : ([
          ["Mức kiểm", p.level || "II"],
          ["AQL nặng / nhẹ", `${p.aqlMajor ?? "—"} / ${p.aqlMinor ?? "—"}`],
        ] as Array<[string, string]>)),
    ["Cỡ mẫu", num(p.sampleSize)],
    ["Ac/Re nặng", p.reMajor ? `${p.acMajor ?? 0}/${p.reMajor}` : "—"],
    ["Ac/Re nhẹ", p.reMinor ? `${p.acMinor ?? 0}/${p.reMinor}` : "—"],
  ];
  return (
    <table className="qc-print-table mb-2 w-full border-collapse border border-black text-[9pt]" data-testid="qc-print-sampling">
      <tbody>
        <tr className="qc-print-small bg-slate-100 text-center text-[7.5pt] font-bold">
          <td className="border border-black px-1 py-0.5 text-left" rowSpan={2}>
            Lấy mẫu
            <br />
            <span className="font-normal">ISO 2859-1</span>
          </td>
          {cells.map(([label]) => (
            <td key={label} className="border border-black px-1 py-0.5">
              {label}
            </td>
          ))}
        </tr>
        <tr className="text-center font-mono font-bold">
          {cells.map(([label, value]) => (
            <td key={label} className="border border-black px-1 py-0.5">
              {value}
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  );
}

function resultText(item: any): string {
  if (isQuantitativeCriterion(item)) {
    const v = item.measuredValue;
    if (v !== undefined && v !== null && v !== "") return `${v}${item.unit ? ` ${item.unit}` : ""}`;
    return "—";
  }
  // Định tính: kết quả chính là đánh giá
  return "";
}

export function QcPrintCriteriaTable({ items, title = "Chỉ tiêu kiểm tra" }: { items: any[]; title?: string }) {
  return (
    <div className="mb-2">
      <div className="mb-0.5 text-[9.5pt] font-bold uppercase">{title}</div>
      <table className="qc-print-table w-full table-fixed border-collapse border border-black text-[9pt]" data-testid="qc-print-criteria">
        <thead>
          <tr className="bg-slate-100 text-center font-bold">
            <th className="border border-black px-1 py-0.5" style={{ width: "4%" }}>#</th>
            <th className="border border-black px-1 py-0.5 text-left" style={{ width: "32%" }}>Chỉ tiêu</th>
            <th className="border border-black px-1 py-0.5" style={{ width: "9%" }}>Loại</th>
            <th className="border border-black px-1 py-0.5 text-left" style={{ width: "27%" }}>Tiêu chuẩn</th>
            <th className="border border-black px-1 py-0.5" style={{ width: "9%" }}>Kết quả</th>
            <th className="border border-black px-1 py-0.5" style={{ width: "8%" }}>Đánh giá</th>
            <th className="border border-black px-1 py-0.5 text-left" style={{ width: "11%" }}>Ghi chú</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={7} className="border border-black px-1 py-1 text-center">
                Chưa ghi chỉ tiêu chi tiết.
              </td>
            </tr>
          ) : (
            items.map((item: any, idx: number) => {
              const quant = isQuantitativeCriterion(item);
              const r = String(item.result || (item.passed === true ? "PASS" : item.passed === false ? "FAIL" : "PENDING")).toUpperCase();
              const sev = item.defectType ? SEVERITY_LABEL[item.defectType] || "" : "";
              return (
                <tr key={item.id || idx} className="break-inside-avoid leading-[1.15]">
                  <td className="border border-black px-1 py-0.5 text-center">{idx + 1}</td>
                  <td className="border border-black px-1 py-0.5">
                    <span className="font-semibold">{displayCriterionName(item, idx)}</span>
                    {sev ? <span className="text-[7.5pt] text-slate-600"> · {sev}</span> : null}
                  </td>
                  <td className="qc-print-small whitespace-nowrap border border-black px-1 py-0 text-center text-[8pt]">{quant ? "Định lượng" : "Định tính"}</td>
                  <td className={`border border-black px-1 py-0.5 ${quant ? "font-mono" : ""}`}>{formatCriterionStandard(item)}</td>
                  <td className="border border-black px-1 py-0.5 text-center font-mono">{resultText(item)}</td>
                  <td className={`border border-black px-1 py-0.5 text-center font-bold ${r === "FAIL" ? "text-rose-800" : ""}`}>
                    {r === "PENDING" ? "—" : QC_RESULT_LABELS[r] || r}
                  </td>
                  <td className="qc-print-small border border-black px-1 py-0.5 text-[8pt]">{item.note || ""}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

export function QcPrintDefects({ items }: { items: any[] }) {
  const rows = (Array.isArray(items) ? items : []).filter((d) => d && typeof d === "object");
  if (rows.length === 0) return null;
  return (
    <div className="mb-2">
      <div className="mb-0.5 text-[9.5pt] font-bold uppercase">Lỗi phát hiện</div>
      <table className="qc-print-table w-full border-collapse border border-black text-[9pt]" data-testid="qc-print-defects">
        <thead>
          <tr className="bg-slate-100 text-center font-bold">
            <th className="border border-black px-1 py-0.5" style={{ width: "14%" }}>Mã lỗi</th>
            <th className="border border-black px-1 py-0.5 text-left">Tên lỗi</th>
            <th className="border border-black px-1 py-0.5" style={{ width: "13%" }}>Mức</th>
            <th className="border border-black px-1 py-0.5" style={{ width: "10%" }}>Số lượng</th>
            <th className="border border-black px-1 py-0.5 text-left" style={{ width: "24%" }}>Ghi chú</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d: any, idx: number) => (
            <tr key={idx}>
              <td className="border border-black px-1 py-0.5 text-center font-mono font-bold">{d.defectCode || d.code}</td>
              <td className="border border-black px-1 py-0.5">{d.defectName || d.name}</td>
              <td className="border border-black px-1 py-0.5 text-center">{SEVERITY_LABEL[d.severity] || d.severity || "Nặng"}</td>
              <td className="border border-black px-1 py-0.5 text-center font-mono font-bold">
                {formatNumber(Number(d.defectQty ?? d.qty ?? d.count ?? 0))}
              </td>
              <td className="qc-print-small border border-black px-1 py-0.5 text-[8pt]">{d.note || ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const STATUS_PRINT: Record<string, string> = {
  PASSED: "ĐẠT",
  FAILED: "KHÔNG ĐẠT",
  WARNING: "NHÂN NHƯỢNG",
  REWORK: "LÀM LẠI",
  RE_INSPECT: "KIỂM LẠI",
  INSPECTING: "ĐANG KIỂM",
  PENDING: "CHỜ KIỂM",
};

export function QcPrintConclusion({
  status,
  counts,
  reMajor,
  reMinor,
  extra,
  overrideReason,
  notes,
}: {
  status?: string | null;
  counts: { critical: number; major: number; minor: number };
  reMajor?: number | null;
  reMinor?: number | null;
  extra?: React.ReactNode;
  overrideReason?: string | null;
  notes?: string | null;
}) {
  const st = String(status || "").toUpperCase();
  const label = STATUS_PRINT[st] || st || "—";
  const tone = st === "PASSED" ? "text-emerald-800" : st === "WARNING" ? "text-amber-800" : st === "FAILED" ? "text-rose-800" : "";
  return (
    <div className="mb-2 border border-black text-[9pt]" data-testid="qc-print-conclusion">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 px-1.5 py-1">
        <b className="uppercase">Kết luận:</b>
        <span className={`text-[10.5pt] font-extrabold ${tone}`}>{label}</span>
        <span>
          Nghiêm trọng <b>{counts.critical}</b> · Nặng <b>{counts.major}</b>
          {reMajor ? `/Re ${reMajor}` : ""} · Nhẹ <b>{counts.minor}</b>
          {reMinor ? `/Re ${reMinor}` : ""}
        </span>
        {extra ? <span>{extra}</span> : null}
      </div>
      {(st === "WARNING" && overrideReason) || notes ? (
        <div className="border-t border-black px-1.5 py-0.5">
          {st === "WARNING" && overrideReason ? (
            <div>
              <b>Lý do nhân nhượng:</b> {overrideReason}
            </div>
          ) : null}
          {notes ? (
            <div>
              <b>Nhận xét:</b> {notes}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function QcPrintLegacy({ rows }: { rows: Array<[string, React.ReactNode]> }) {
  const visible = rows.filter(([, v]) => v !== null && v !== undefined && v !== "");
  if (visible.length === 0) return null;
  return (
    <div className="mb-2 border border-dashed border-black px-1.5 py-0.5 text-[8.5pt]" data-testid="qc-print-legacy">
      <b>Số đo (dữ liệu cũ):</b> {visible.map(([l, v]) => `${l} ${v}`).join(" · ")}
    </div>
  );
}

export function QcPrintSignatures({ columns }: { columns: Array<{ title: string; hint: string; name?: string }> }) {
  return (
    <div className="mt-2 grid grid-cols-3 gap-4 text-center text-[9.5pt] break-inside-avoid">
      {columns.map((c) => (
        <div key={c.title}>
          <div className="font-bold uppercase">{c.title}</div>
          <div className="text-[8pt] italic text-slate-500">{c.hint}</div>
          <div className="h-12" />
          <div className="font-bold">{c.name || " "}</div>
        </div>
      ))}
    </div>
  );
}

/** Tóm tắt đóng gói 1 dòng: 12 thùng × 500 + 1 thùng lẻ 230 = 6.230 */
export function packingSummary(qty: number, pcsPerBox: number): string {
  const per = Number(pcsPerBox) || 0;
  const q = Math.max(0, Number(qty) || 0);
  if (per <= 0 || q <= 0) return "";
  const full = Math.floor(q / per);
  const rest = q % per;
  return `${full} thùng × ${formatNumber(per)}${rest > 0 ? ` + 1 thùng lẻ ${formatNumber(rest)}` : ""} = ${formatNumber(q)}`;
}
